"use client";

import * as React from "react";
import * as Popover from "@radix-ui/react-popover";
import { cn } from "@/lib/utils";

// ── Types ────────────────────────────────────────────────────────────────────
export interface AppMenuItem {
  label: string;
  icon?: React.ReactNode;
  onClick?: () => void;
  destructive?: boolean;
  disabled?: boolean;
}

export interface AppMenuProps {
  items: AppMenuItem[];
  trigger: React.ReactNode;
  align?: "start" | "center" | "end";
  itemSize?: "default" | "xl";
}

// ── Component ────────────────────────────────────────────────────────────────
export function OptionMenu({ items, trigger, align = "end", itemSize = "default" }: AppMenuProps) {
  const [open, setOpen] = React.useState(false);

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>{trigger}</Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align={align}
          sideOffset={6}
          className={cn(
            "z-50 min-w-[140px] rounded-xl border border-border bg-background shadow-md",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
            "p-1"
          )}
        >
          {items.map((item, i) => (
            <button
              key={i}
              disabled={item.disabled}
              onClick={() => {
                item.onClick?.();
                setOpen(false);
              }}
              className={cn(
                "flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                itemSize === "xl" ? "h-16 px-4 text-base" : "py-2 text-sm",
                "hover:bg-muted disabled:pointer-events-none disabled:opacity-50",
                item.destructive ? "text-destructive hover:bg-destructive/10" : "text-foreground"
              )}
            >
              {item.icon && <span className="shrink-0">{item.icon}</span>}
              {item.label}
            </button>
          ))}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
